import { MersenneTwister } from "./mersenne-twister";

export async function asyncForEach<T>(
  array: T[],
  callback: (item: T, index: number) => Promise<void>
): Promise<void> {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index);
  }
}

export function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min)) + min;
}

export function removeRandomArrayElement<T>(array: T[], random: MersenneTwister): T {
  const rand = random.randomIntInRange(0, array.length);
  return array.splice(rand, 1)[0];
}

export function shuffleArray<T>(array: T[], random: MersenneTwister): void {
  for (let i = array.length - 1; i > 0; i--) {
    const j = random.randomIntInRange(0, i + 1);
    [array[i], array[j]] = [array[j], array[i]];
  }
}

export const noOp = () => ({});

export type TippyTheme =
  | 'noSelection'
  | 'action'
  | 'review-correct'
  | 'review-incorrect'
  | 'tagPositive'
  | 'tagNegative'
  | 'distractor-tag'
  | 'correct-tag'
  | 'tag-confusion'
  | '';

export const ITS_CFG = {
  displayMode: true, // is this running as the app or just displaying data
};

export function endsWithPunctuation(s: string): boolean {
  return '.?!'.indexOf(s.slice(-1)) >= 0;
}

export function addPeriod(s: string): string {
  return s + (endsWithPunctuation(s) ? '' : '.');
}
export function removePeriod(s: string): string {
  return s.endsWith('.') ? s.substring(0, s.length - 1) : s;
}

export function capitalizeFirstCharacter(s: string): string {
  s = s.toLocaleLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export const joinElementList = (
  strings: string[],
  conjunction: string
): string => {
  switch (strings.length) {
    case 0: {
      return '';
    }
    case 1: {
      return capitalizeFirstCharacter(strings[0]);
    }
    case 2: {
      return capitalizeFirstCharacter(strings.join(` **${conjunction}** `));
    }
    default: {
      const joinString = ', ';
      const joined = strings.join(joinString);
      const pos = joined.lastIndexOf(joinString);
      const ret = capitalizeFirstCharacter(
        `${joined.substring(0, pos)}, **${conjunction}** ${joined.substring(
          pos
        )}`
      );
      return ret;
    }
  }
};

export const bulletList = (strings: string[]): string => {
  if (strings.length <= 1) {
    return strings[0] || '';
  }
  return strings.reduce((pv, cv) => `${pv}* ${cv}\n`, '');
};

export enum ScreenSize {
  xs = 0,
  sm,
  md,
  lg,
  xl,
}

export function getScreenSize(): ScreenSize {
  const width = Math.max(
    document.documentElement.clientWidth,
    window.innerWidth || 0
  );

  if (width <= 576) return ScreenSize.xs;
  if (width <= 768) return ScreenSize.sm;
  if (width <= 992) return ScreenSize.md;
  if (width <= 1200) return ScreenSize.lg;
  return ScreenSize.xl;
}
